'use client';

import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/auth';
import { useNotificationsStore } from '@/stores/notifications';
import { usePathname } from 'next/navigation';
import { X } from 'lucide-react';
import { useTranslations } from 'next-intl';

const PUSH_BANNER_DISMISSED_KEY = 'push_notifications_banner_dismissed';

export function PushNotificationManager() {
  const authStore = useAuthStore();
  const notificationsStore = useNotificationsStore();
  const pathname = usePathname();
  const t = useTranslations('common');
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    if (!authStore.isLoggedIn || authStore.authLoading || !authStore.user) return;
    if (authStore.user.has_fcm_tokens) {
      setShowBanner(false);
      return;
    }
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(PUSH_BANNER_DISMISSED_KEY)) {
      setShowBanner(false);
      return;
    }

    const isMasterAdmin = authStore.user.is_master_admin === true;
    const isOrgAdmin = (authStore.user.memberships ?? []).some(m => m.role === 'admin');
    const isOrgMember = (authStore.user.memberships ?? []).some(m => m.role === 'member');

    if (isMasterAdmin || isOrgAdmin || isOrgMember) {
      setShowBanner(true);
    }
  }, [authStore.isLoggedIn, authStore.authLoading, authStore.user, pathname]);

  const dismissBanner = () => {
    setShowBanner(false);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(PUSH_BANNER_DISMISSED_KEY, '1');
    }
  };

  const handleEnablePush = async () => {
    setShowBanner(false);
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(PUSH_BANNER_DISMISSED_KEY, '1');
    }
    await notificationsStore.initializeFCM();
    authStore.fetchUser({ silent: true });
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm bg-white dark:bg-slate-800 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 p-4 animate-in slide-in-from-bottom-5 fade-in duration-300">
      <button 
        onClick={dismissBanner}
        className="absolute top-2 right-2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        aria-label={t('close')}
      >
        <X size={16} />
      </button>
      <h4 className="font-semibold text-slate-900 dark:text-slate-100 mb-1 pr-6">
        {t('notifications.enablePushTitle') || 'Enable Notifications'}
      </h4>
      <p className="text-sm text-slate-600 dark:text-slate-400 mb-3">
        {t('notifications.enablePushDescription') || 'Stay updated with your organization activity.'}
      </p>
      <div className="flex justify-end gap-2">
        <button
          onClick={dismissBanner}
          className="text-sm px-3 py-1.5 text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-md transition-colors"
        >
          {t('later') || 'Later'}
        </button>
        <button
          onClick={handleEnablePush}
          className="text-sm px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors shadow-sm"
        >
          {t('enable') || 'Enable'}
        </button>
      </div>
    </div>
  );
}
